import { http } from '../http';
import { extrairMensagemErro } from '../../utils/apiError';

export interface TermoTemplate {
  id?: string;
  codigo: string;
  /** Nome do TIPO de termo (rótulo das telas) — diferente do `titulo`, que vai no PDF. */
  nome: string;
  titulo: string;
  corpo: string;
  mostrarEndereco: boolean;
  mostrarProdutos: boolean;
  mostrarValores: boolean;
  listarProdutos: boolean;
  assinaturaConsultor: boolean;
  ativo: boolean;
  atualizadoPorId?: string | null;
  criadoEm?: string;
  atualizadoEm?: string;
  /** Derivados do backend — a tela não recalcula (convenção at-24/at-32). */
  transcricaoPendente?: boolean;
  geravel?: boolean;
}

/**
 * Tipo de termo que o SAAC sabe gerar — o que a tela `/termo` oferece ao consultor.
 * Vem de `GET /termo-template/tipos`, que substituiu `GET /sga-termo/listar` quando o
 * catálogo deixou de ser espelho do SGA (at-40).
 */
export interface TipoDeTermo {
  id: string;
  codigo: string;
  nome: string;
  titulo: string;
  geravel: boolean;
}

export interface UpsertTermoTemplate {
  nome?: string;
  titulo: string;
  corpo: string;
  mostrarEndereco?: boolean;
  mostrarProdutos?: boolean;
  mostrarValores?: boolean;
  listarProdutos?: boolean;
  assinaturaConsultor?: boolean;
  ativo?: boolean;
}

export interface TermoTemplateHistorico {
  id: string;
  termoTemplateId: string;
  codigo: string;
  titulo: string;
  corpo: string;
  mostrarEndereco: boolean;
  mostrarProdutos: boolean;
  mostrarValores: boolean;
  assinaturaConsultor: boolean;
  ativo: boolean;
  atualizadoPorId: string | null;
  criadoEm: string;
}

export interface TermoTemplatePendencias {
  tiposSemTemplate: number;
  codigos: string[];
}

export interface PlaceholderTermo {
  chave: string;
  rotulo: string;
  descricao: string;
}

/**
 * Placeholders que o motor sabe resolver. Vêm do backend de propósito: a lista
 * local que existia aqui podia anunciar um campo que o PDF não preenchia.
 */
export const listarPlaceholders = async (): Promise<PlaceholderTermo[]> => {
  try {
    const { data } = await http.get('/termo-template/placeholders');
    return data;
  } catch (error) {
    throw new Error(extrairMensagemErro(error, 'Erro ao listar placeholders disponíveis'));
  }
};

/** Catálogo completo (TI/ADMIN): todos os tipos, com o estado de cada template. */
export const listarTemplates = async (): Promise<TermoTemplate[]> => {
  try {
    const { data } = await http.get('/termo-template');
    return data;
  } catch (error) {
    throw new Error(extrairMensagemErro(error, 'Erro ao listar os tipos de termo'));
  }
};

/** Tipos GERÁVEIS (qualquer autenticado) — alimenta a tela de geração do consultor. */
export const listarTiposDeTermo = async (): Promise<TipoDeTermo[]> => {
  try {
    const { data } = await http.get('/termo-template/tipos');
    return data;
  } catch (error) {
    throw new Error(extrairMensagemErro(error, 'Erro ao listar os tipos de termo'));
  }
};

/**
 * Cadastra um tipo de termo novo. O código é gerado pelo backend; o tipo nasce inativo
 * e sem texto, aparecendo como transcrição pendente até as cláusulas serem escritas.
 */
export const criarTipoDeTermo = async (nome: string): Promise<TermoTemplate> => {
  try {
    const { data } = await http.post('/termo-template/tipo', { nome });
    return data;
  } catch (error) {
    throw new Error(extrairMensagemErro(error, 'Erro ao cadastrar o tipo de termo'));
  }
};

export const buscarTemplate = async (codigo: string): Promise<TermoTemplate> => {
  try {
    const { data } = await http.get(`/termo-template/${codigo}`);
    return data;
  } catch (error) {
    throw error;
  }
};

export const salvarTemplate = async (
  codigo: string,
  payload: UpsertTermoTemplate
): Promise<TermoTemplate & { warnings?: string[] }> => {
  try {
    const { data } = await http.put(`/termo-template/${codigo}`, payload);
    return data;
  } catch (error) {
    throw new Error(extrairMensagemErro(error, 'Erro ao salvar template de termo'));
  }
};

export const historicoTemplate = async (
  codigo: string
): Promise<TermoTemplateHistorico[]> => {
  try {
    const { data } = await http.get(`/termo-template/${codigo}/historico`);
    return data;
  } catch (error) {
    throw new Error(extrairMensagemErro(error, 'Erro ao buscar histórico do template'));
  }
};

export const pendenciasTemplates = async (): Promise<TermoTemplatePendencias> => {
  try {
    const { data } = await http.get('/termo-template/pendencias');
    return data;
  } catch (error) {
    throw new Error(extrairMensagemErro(error, 'Erro ao consultar pendências de templates'));
  }
};

export const previewTemplate = async (
  payload: UpsertTermoTemplate
): Promise<Blob> => {
  try {
    const { data } = await http.post('/termo-template/preview', payload, {
      responseType: 'blob',
    });
    return data;
  } catch (error) {
    throw new Error(extrairMensagemErro(error, 'Erro ao gerar pré-visualização do template'));
  }
};
